"use client"

import React, { useState } from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AtsFieldPathPicker } from "@/components/shared/ats-field-path-picker"
import type { AtsFieldDescriptor } from "@/types/ats-explorer-api"
import { ArrayPathRefiner } from "@/components/shared/array-path-refiner"
import type { Entity } from "@/types/fbo-api"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export interface ReferenceIdBuilderProps {
  entities: Entity[]
  onReferenceIdBuilt: (referenceId: string) => void
}

/**
 * Assembles a non-primary entity's Reference ID ("{EntityName}.{path.to.id}") from
 * an existing entity plus an ID path picked from the entity's live ATS schema. The
 * picker expands association nodes in place ("jobOrder" → "jobOrder.clientCorporation.id"),
 * so IDs the parent schema doesn't list directly stay reachable. When the ID lives
 * inside an object array (e.g., job_profiles[].company.id), the shared ArrayPathRefiner
 * (single-value mode) picks the first element or the element matching a condition —
 * generating a JSONPath filter expression without exposing its syntax. Bare "[]"
 * resolves as the first element server-side.
 */
/** Reference IDs point at identifiers: "id", "company.id", "company_id" or "companyId" (associations expand to theirs) */
const isIdPath = (field: AtsFieldDescriptor) => /(^|\.|_)id$/i.test(field.path) || /[a-z0-9]Id$/.test(field.path)

export function ReferenceIdBuilder({ entities, onReferenceIdBuilt }: ReferenceIdBuilderProps) {
  const { t } = useTranslation()
  const [sourceEntityName, setSourceEntityName] = useState("")
  // The path picked, and the (possibly refined) path emitted
  const [selectedIdPath, setSelectedIdPath] = useState("")
  const [refinedPath, setRefinedPath] = useState("")

  const sourceEntityType = entities.find((e) => e.name === sourceEntityName)?.entityType ?? ""

  const handleSelectSourceEntity = (entityName: string) => {
    setSourceEntityName(entityName)
    setSelectedIdPath("")
    setRefinedPath("")
  }

  const handleSelectIdPath = (path: string | null) => {
    const next = path ?? ""
    setSelectedIdPath(next)
    setRefinedPath(next)
    if (next) onReferenceIdBuilt(`${sourceEntityName}.${next}`)
  }

  const handleRefine = (path: string) => {
    setRefinedPath(path)
    onReferenceIdBuilt(`${sourceEntityName}.${path}`)
  }

  return (
    <div className="mt-4 border rounded-md p-4 bg-muted/30 space-y-3">
      <div>
        <p className="text-sm font-medium mb-1">{t("referenceIdBuilder.card.title")}</p>
        <p className="text-xs text-muted-foreground">
          {t("referenceIdBuilder.card.description")}
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Select value={sourceEntityName || undefined} onValueChange={handleSelectSourceEntity}>
          <SelectTrigger className="w-56 h-9">
            <SelectValue placeholder={t("referenceIdBuilder.placeholders.sourceEntity")} />
          </SelectTrigger>
          <SelectContent>
            {entities.map((entity) => (
              <SelectItem key={entity.name} value={entity.name}>
                {entity.name} ({entity.entityType})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {sourceEntityType && (
          <div className="w-80">
            <AtsFieldPathPicker
              key={sourceEntityName}
              entityType={sourceEntityType}
              value={selectedIdPath || null}
              onChange={handleSelectIdPath}
              placeholder={t("referenceIdBuilder.placeholders.idField")}
              filter={isIdPath}
            />
          </div>
        )}
      </div>

      {/* Which element of the array supplies the ID — shared refiner, single-value mode */}
      {refinedPath.includes("[") && sourceEntityType && (
        <ArrayPathRefiner
          entityType={sourceEntityType}
          path={refinedPath}
          onChange={handleRefine}
          selection="single"
        />
      )}
    </div>
  )
}
